import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';

import Loader from '../Loader/Loader';
import type { ExpertData } from '@/types/expert';
import ArrowRightIcon from '../Icons/Action/ArrowRightIcon';
import { useSelectedCategories } from '@/redux/selectors/categorySelector';
import MainExpertCard from '../Experts/MainExpertCard';
import { getExperts } from '@/services/strapi/strapiCategoryService';
import { T } from '@tolgee/react';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';

const NextArrow = ({ onClick, isVisible }: { onClick?: () => void; isVisible: boolean }) => (
  <div
    className={`absolute right-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-[100px] border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center transition-opacity duration-300 ${
      isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
    }`}
    onClick={onClick}
  >
    <ArrowRightIcon width={20} height={20} />
  </div>
);

const PrevArrow = ({ onClick, currentSlide }: { onClick?: () => void; currentSlide: number }) =>
  currentSlide > 0 ? (
    <div
      className="absolute left-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-[100px] border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center"
      onClick={onClick}
    >
      <ArrowRightIcon width={20} height={20} className="transform rotate-180" />
    </div>
  ) : null;

const ExpertSlider: React.FC = () => {
  const [experts, setExperts] = useState<ExpertData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const selectedCategories = useSelectedCategories();
  const locale = useSelectedLanguage();

  useEffect(() => {
    const fetchExperts = async () => {
      setLoading(true);
      try {
        const categorySlugs = selectedCategories.filter((category) => category !== 'all');

        const response = await getExperts(5, 0, categorySlugs, locale);

        const mappedExperts: ExpertData[] = response.data.map((expert: ExpertData) => ({
          id: expert.id,
          attributes: {
            name: expert.attributes.name,
            createdAt: expert.attributes.createdAt,
            updatedAt: expert.attributes.updatedAt,
            publishedAt: expert.attributes.publishedAt,
            picture: expert.attributes.picture,
            external_id: expert.attributes.external_id,
            type: expert.attributes.type,
            description: expert.attributes.description,
            languages: expert.attributes.languages || [],
            categories: expert.attributes.categories || [],
          },
        }));

        setExperts(mappedExperts);
      } catch (error) {
        console.error('Error fetching experts:', error);
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    };

    void fetchExperts();
  }, [selectedCategories, locale]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const slideCount = experts.length;
  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1.06,
    slidesToScroll: 1,
    centerMode: false,
    nextArrow: <NextArrow isVisible={currentSlide < slideCount - 1} />,
    prevArrow: <PrevArrow currentSlide={currentSlide} />,
    beforeChange: (current: number, next: number) => setCurrentSlide(Math.round(next)),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1.06,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.2,
          slidesToScroll: 1,
        },
      },
    ],
  };

  if (loading) return <Loader position={'absolute'} />;
  if (error) return <div>Error loading experts</div>;
  if (!experts.length) return null;
  return (
    <div className="relative p-5 md:p-12 pr-0 mb-3 w-full">
      <div className="relative">
        <h2 className="text-4xl">
          <T>home.filtered.experts_section.title.label</T>
        </h2>
      </div>
      <Slider {...settings} className="mt-4">
        {experts.map((expert, index) => (
          <div key={index}>
            <MainExpertCard
              imageUrl={expert.attributes.picture.data?.attributes.url || ''}
              name={expert.attributes.name}
              title={expert.attributes.type}
              description={expert.attributes.description}
              tags={expert.attributes.categories.data.map((category) => category.attributes.Name)}
              experience={
                expert.attributes.languages.data
                  ?.map((language) => language.attributes.name)
                  ?.join(', ') || ''
              }
              expertId={expert.attributes.external_id}
              id={expert.id}
              onOpenModal={() => {}}
            />
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default ExpertSlider;
